<template>
  <div class="card card-flush" :class="compact ? 'sncr-section-compact' : 'mb-5 mb-xl-8'">
    <div class="card-header" :class="{'px-0 min-h-auto pb-5': compact}">
      <h3 v-if="!compact" class="card-title fw-bold">
        <i class="bi bi-shield-check me-2"></i>
        Numerações da Anvisa (SNCR)
      </h3>
      <div class="card-toolbar">
        <button type="button" class="btn btn-sm btn-primary" :disabled="starting" @click="authenticate">
          {{ starting ? 'Abrindo gov.br...' : 'Autenticar no gov.br' }}
        </button>
      </div>
    </div>
    <div class="card-body pt-5">
      <div class="sncr-summary">
        <i class="bi bi-info-circle-fill"></i>
        <div>
          <strong>Emita receitas controladas sem interromper o atendimento.</strong>
          <p>Cada documento consome uma numeração oficial. A Ubbimed mantém os números disponíveis para uso e avisa quando for necessário reabastecer.</p>
        </div>
      </div>

      <div v-if="loading" class="text-muted">Consultando seu estoque...</div>

      <div v-else-if="!hasData" class="sncr-empty">
        Nenhuma numeração em estoque e nenhuma emissão controlada nos últimos 90 dias.
        Se você prescreve medicamentos controlados, autentique no gov.br para receber o primeiro bloco.
      </div>

      <div v-else>
        <div class="sncr-inscription" v-for="inscription in inscriptions" :key="inscriptionKey(inscription)">
          <p class="sncr-inscription-label">
            {{ inscription.council }} {{ inscription.council_document }}/{{ inscription.council_uf }}
          </p>
          <table class="table table-row-bordered gy-3 mb-0">
            <thead>
            <tr class="fw-bold fs-7 text-muted">
              <th>Receituário</th>
              <th class="text-center">Em estoque</th>
              <th class="text-center">Situação</th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="type in inscription.types" :key="type.regulatory_type">
              <td>
                {{ type.regulatory_type_description }}
                <span v-if="type.requires_visa_authorization" class="sncr-visa" title="A numeração deste tipo depende de autorização prévia da vigilância sanitária">
                  depende da Visa
                </span>
              </td>
              <td class="text-center">{{ type.balance }}</td>
              <td class="text-center">
                <span class="sncr-status" :class="'sncr-status-' + meta(type.status).tone">
                  <i class="bi" :class="meta(type.status).icon"></i>
                  {{ meta(type.status).label }}
                </span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

        <p class="sncr-watermark">
          <i class="bi bi-bell me-1"></i>
          Na lista de atendimentos, mostramos um aviso quando restarem {{ balance.low_watermark }} números ou menos.
          Com {{ balance.critical_watermark }} ou menos, o aviso fica crítico e não pode ser dispensado.
          Também enviamos um e-mail ao atingir esses dois limites e quando o saldo zerar.
        </p>
      </div>
    </div>
  </div>
</template>

<script>
import Alert from "../../helpers/alert";
import {startGovBrAuth, statusMeta} from "../../helpers/sncr";

export default {
  name: "SncrSection",
  props: {
    professionalId: [Number, String],
    compact: {type: Boolean, default: false},
  },
  data() {
    return {
      starting: false,
    }
  },
  mounted() {
    this.$store.dispatch('sncr/load', {professionalId: this.professionalId, force: true});
  },
  computed: {
    balance() {
      return this.$store.state.sncr.balance ?? {};
    },
    loading() {
      return this.$store.state.sncr.loading;
    },
    hasData() {
      return this.$store.getters['sncr/hasData'];
    },
    inscriptions() {
      return this.balance.inscriptions ?? [];
    },
  },
  methods: {
    meta(status) {
      return statusMeta(status);
    },
    inscriptionKey(inscription) {
      return `${inscription.council}-${inscription.council_document}-${inscription.council_uf}`;
    },
    authenticate() {
      this.starting = true;

      startGovBrAuth(this.professionalId, {path: this.$route.fullPath, professionalId: this.professionalId}).catch(() => {
        this.starting = false;
        Alert.toastError('Não foi possível abrir a autenticação do gov.br. Tente novamente.');
      });
    },
  },
}
</script>

<style scoped>
.sncr-section-compact {
  border: 0;
  box-shadow: none;
}

.sncr-section-compact .card-body {
  padding: 0;
}

.sncr-empty {
  border: 1px dashed #ced4da;
  border-radius: 8px;
  padding: 12px 16px;
  color: #495057;
}

.sncr-summary {
  display: flex;
  gap: 12px;
  padding: 14px 16px;
  margin-bottom: 18px;
  border: 1px solid #cbdcf3;
  border-radius: 10px;
  color: #173b69;
  background: #edf4fc;
}

.sncr-summary > i {
  margin-top: 1px;
  color: #285f9d;
  font-size: 18px;
}

.sncr-summary p {
  margin: 3px 0 0;
  color: #344f70;
}

.sncr-inscription {
  margin-bottom: 20px;
}

.sncr-inscription-label {
  font-weight: 700;
  color: #212529;
  margin-bottom: 4px;
}

.sncr-visa {
  font-size: 0.7rem;
  color: #7a4f00;
  background: #fff4e0;
  border-radius: 6px;
  padding: 2px 6px;
  margin-left: 6px;
}

.sncr-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  font-size: 0.8rem;
}

.sncr-status-comfortable {
  color: #2b8a3e;
}

.sncr-status-low {
  color: #7a4f00;
}

.sncr-status-critical,
.sncr-status-empty {
  color: #c92a2a;
}

.sncr-watermark {
  font-size: 0.85rem;
  margin: 0;
  color: #475467;
}
</style>
